import { useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'

/**
 * Hook that adds "click any field to edit it" behaviour to a detail page.
 *
 * Callers wrap the read-mode value of each editable field with the props
 * returned by `bindReadOnly(field)`. When the user clicks (or presses
 * Enter / Space on a focused) read-mode value, the hook:
 *   1. queues that field for focus,
 *   2. invokes `onEnterEditMode` (typically the page's existing handler
 *      such as `handleStartEdit` / `handleEditClick`),
 *   3. once the edit-mode JSX has rendered, focuses the matching input
 *      and selects its text.
 *
 * The page's existing Save / Cancel flow and edit-mode JSX are unchanged.
 *
 * Each input/select/textarea must have `id={field}` set so the hook can
 * locate it via `document.getElementById`. Most existing helpers already
 * do this; the few that don't need a one-line addition.
 *
 * @example
 *   const { bindReadOnly } = useClickToEdit(isEditMode, handleStartEdit)
 *   // In the read-mode branch of your renderField helper:
 *   return <div {...bindReadOnly('amount')}>{value || 'N/A'}</div>
 */
export function useClickToEdit(isEditMode: boolean, onEnterEditMode: () => void) {
  const [focusField, setFocusField] = useState<string | null>(null)

  useEffect(() => {
    if (!isEditMode || !focusField) return
    // Defer to the next tick so the edit-mode JSX has mounted and
    // <input id={field}> exists in the DOM.
    const timer = setTimeout(() => {
      const el = document.getElementById(focusField) as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement
        | null
      if (el) {
        el.focus()
        if ('select' in el && typeof (el as HTMLInputElement).select === 'function') {
          try {
            ;(el as HTMLInputElement).select()
          } catch (_) {
            // select() throws for some input types (e.g. number, date)
            // - safe to ignore.
          }
        }
      }
      setFocusField(null)
    }, 0)
    return () => clearTimeout(timer)
  }, [isEditMode, focusField])

  function activate(field: string) {
    setFocusField(field)
    if (!isEditMode) onEnterEditMode()
  }

  /**
   * Spread on the read-mode element wrapping a field's value.
   *
   * @param field   the input id (must match `<input id={field}>` in edit
   *                mode)
   * @param label   optional human-readable label used in the aria-label;
   *                falls back to the field id
   */
  function bindReadOnly(field: string, label?: string) {
    return {
      role: 'button' as const,
      tabIndex: 0,
      onClick: () => activate(field),
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          activate(field)
        }
      },
      className:
        'cursor-pointer rounded px-1 -mx-1 transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      'aria-label': `Edit ${label || field}`,
    }
  }

  return { bindReadOnly, setFocusField }
}
